import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferCalendarMonthPicker from '../CalendarPicker/MonthPicker/OfferCalendarMonthPicker'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageState, OfferPageFilterType, PaginatorState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions, takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import OfferBundleAndSaveCalendarSidebarDayPicker from './OfferBundleAndSaveCalendarSidebarDayPicker'
import OfferCalendarNotAvailableMessage from '../CalendarPicker/OfferCalendarNotAvailableMessage'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import OfferCalendarDurationSelect from '../CalendarPicker/CalendarDuration/OfferCalendarDurationSelect'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import CalendarPickerRecommendationsSidebar from '../CalendarPicker/Recommendations/CalendarPickerRecommendationsSidebar'
import { CalendarRecommendationsViewProvider, useCalendarRecommendationsView } from '../CalendarPicker/CalendarRecommendationsViewContext'
import Group from 'components/utils/Group'
import OfferCalendarBundlePackageSelect from '../CalendarPicker/CalendarBundlePackages/OfferCalendarBundlePackageSelect'
import OfferCalendarPointsToggle from '../CalendarPicker/OfferCalendarPointsToggle'
import OfferCalendarHeaderExtensionContainer from '../CalendarPicker/OfferCalendarHeaderExtensionContainer'
import { CalendarBurnProvider } from '../CalendarPicker/CalendarBurnContext'
import useCalendarBurnToggleVisible from '../CalendarPicker/useCalendarBurnToggleVisible'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import OfferFilterBundleSidebarBottomPanel from '../Sidebar/OfferFilterBundleSidebarBottomPanel'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import { getCalendarMonths, isFetchingCalendars, getSelectedOfferPageDay } from 'selectors/offerPage/offerPageSelectors'
import { useIntl } from 'react-intl'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

interface Props {
  offer: App.Offer
  bundledOffersState: OfferPageState['bundledOffers']
  fetching?: boolean
  checkIn?: string
  selectedDay?: App.CalendarDay
  calendarError?: boolean
  calendarAvailability: boolean
  defaultDayPicker?: boolean
  defaultMonth?: string
  currentOfferId?: string
  onBack?: () => void
  onClose?: () => void
  onApply?: () => void
  activeFilter?: OfferPageFilterType
  paginator?: PaginatorState
}

function OfferBundleAndSaveCalendarSidebarView(props: Props) {
  const {
    offer,
    bundledOffersState,
    fetching,
    selectedDay,
    checkIn,
    calendarError,
    calendarAvailability,
    defaultDayPicker,
    defaultMonth,
    currentOfferId,
    onBack,
    onClose,
    onApply,
    activeFilter,
    paginator,
  } = props
  const intl = useIntl()
  const isBurnToggleVisible = useCalendarBurnToggleVisible()
  const dateFormatter = useIntlDateFormatter()
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const bundledOffers = useMemo(() => Object.values(bundledOffersState ?? {}).filter(Boolean), [bundledOffersState])

  const [selectedMonth, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)

  const selectCheckInDatesTitle = intl.formatMessage({
    id: 'Kz7JbcZjY1V64DUIJN5-',
    defaultMessage: 'Select check-in dates',
  })
  const selectCheckInMonthTitle = intl.formatMessage({
    id: '9W4onaGBYlB7bbNV1q49',
    defaultMessage: 'Select check-in month',
  })
  const dispatch = useOfferPageDispatch()
  const { view: recommendationsView } = useCalendarRecommendationsView()
  const recommendationsShown = !!recommendationsView && shouldEnableCalendarRecommendations(offer)
  const onMonthSelect = useCallback((monthKey: string) => {
    setMonth(monthKey)
  }, [])

  useEffect(() => {
    if (checkIn && selectedDay && !selectedDay.hotelAvailable) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    }
  }, [checkIn, dispatch, selectedDay, currentOfferId])

  const onBackToMonth = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    setMonth(undefined)
  }, [onUserAnalyticsAction, dispatch, currentOfferId])

  const isBundleAndSaveOffer = bundledOffers.length > 0

  const currentOfferIndex = bundledOffers.findIndex(o => o.offer?.id === paginator?.currentOfferId)
  const currentOffer = bundledOffers[currentOfferIndex]

  const dateLabel = useMemo(() => {
    const currentOffer = bundledOffers.find(o => o.offer?.id === paginator?.currentOfferId)
    const checkInDate = currentOffer?.checkInDate
    const checkOutDate = currentOffer?.checkOutDate
    const duration = currentOffer?.duration

    if (checkInDate) {
      const checkInMoment = moment(checkInDate)
      const formattedCheckIn = dateFormatter(checkInMoment, { day: '2-digit', month: 'short' })
      if (duration) {
        return intl.formatMessage(
          {
            id: 'Ltiw36ARf_XDbsXClEwS',
            defaultMessage: '{checkInDate} - {checkOutDate}',
          },
          {
            checkInDate: formattedCheckIn,
            checkOutDate: dateFormatter(checkInMoment.clone().add(duration, 'days'), { day: '2-digit', month: 'short' }),
          },
        )
      }

      if (checkOutDate) {
        const checkOutMoment = moment(checkOutDate)
        return intl.formatMessage(
          {
            id: '2DJA_hePSTgbiwLcukw-',
            defaultMessage: '{checkInDate} - {checkOutDate}',
          },
          {
            checkInDate: formattedCheckIn,
            checkOutDate: dateFormatter(checkOutMoment, { day: '2-digit', month: 'short' }),
          },
        )
      }
    }

    return null
  }, [bundledOffers, dateFormatter, intl, paginator?.currentOfferId])

  const noDatesSelectedLabel = intl.formatMessage({
    id: 'fNm378JbPlwnb5sghxZu',
    defaultMessage: 'No dates selected',
  })

  return (<CalendarBurnProvider>
    <ModalHeader
      title={selectedMonth !== undefined ? selectCheckInDatesTitle : selectCheckInMonthTitle}
      onCloseButtonClick={onClose}
      onBackButtonClick={onBack}
    >
      <Group direction="vertical" horizontalAlign="center" gap={4}>
        <Caption variant="medium" colour="light-foreground-alt">
          {intl.formatMessage(
            {
              id: 'C162Nt_l3TgMV7g6qspl',
              defaultMessage: 'Hotel {currentHotelNumber} of {totalHotels}',
            },
            { currentHotelNumber: currentOfferIndex + 1, totalHotels: bundledOffers.length },
          )}
        </Caption>
        <BodyText variant="medium" weight="bold">
          {currentOffer?.offer?.property?.name}
        </BodyText>
        <Caption variant="medium" colour="light-foreground-alt">
          {dateLabel ?? noDatesSelectedLabel}
        </Caption>
      </Group>
      {!recommendationsShown && <Group direction="horizontal" verticalAlign="center" gap={8}>
        {!isBundleAndSaveV2Enabled() && <OfferCalendarDurationSelect />}
        <OfferCalendarBundlePackageSelect />
      </Group>}
      {!recommendationsShown && <OfferCalendarHeaderExtensionContainer>
        <OfferCalendarPointsToggle />
      </OfferCalendarHeaderExtensionContainer>}
    </ModalHeader>
    <ModalBody>
      <ModalContent>
        {recommendationsShown && <CalendarPickerRecommendationsSidebar />}
        {!recommendationsShown && <div style={isBurnToggleVisible ? { paddingTop: 12 } : undefined}>
          {!fetching && calendarError && !calendarAvailability && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
          {calendarAvailability && <>
            {selectedMonth !== undefined && !fetching && <OfferBundleAndSaveCalendarSidebarDayPicker
              onBackToMonth={onBackToMonth}
              initialMonth={selectedMonth}
              defaultDayPicker={defaultDayPicker}
              flightsOnly={offer.bundledWithFlightsOnly}
            />}
            {selectedMonth === undefined && <OfferCalendarMonthPicker
              onMonthSelect={onMonthSelect}
            />}
          </>}
          <LoadingIndicator visible={!calendarAvailability && isBundleAndSaveOffer} floating />
        </div>}
      </ModalContent>
    </ModalBody>
    <OfferFilterBundleSidebarBottomPanel
      onClick={onApply}
      activeFilter={activeFilter}
    />
  </CalendarBurnProvider>
  )
}

function OfferBundleAndSaveCalendarSidebar(props: Props) {
  return (
    <CalendarRecommendationsViewProvider>
      <OfferBundleAndSaveCalendarSidebarView {...props} />
    </CalendarRecommendationsViewProvider>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    paginator: state.paginator,
    currentOfferId: state.paginator?.currentOfferId,
    calendarAvailability: getCalendarMonths(appState, state).availableCount > 0,
    calendarError: state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    checkIn: state.checkInDate,
    fetching: isFetchingCalendars(appState, state),
    offer: state.offer,
    bundledOffersState: state.bundledOffers,
    selectedDay: getSelectedOfferPageDay(appState, state),
    activeFilter: state.activeFilter,
  }
}, OfferBundleAndSaveCalendarSidebar)
